import { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, HelpCircle, Activity } from 'lucide-react';
import { supabase } from '../../lib/supabase';

type ServiceStatus = 'available' | 'unavailable' | 'unknown' | 'checking';

interface ServiceState {
  status: ServiceStatus;
  lastCheck: Date | null;
}

export function AIServiceStatus() {
  const [openaiState, setOpenaiState] = useState<ServiceState>({
    status: 'unknown',
    lastCheck: null,
  });
  const [leonardoState, setLeonardoState] = useState<ServiceState>({
    status: 'unknown',
    lastCheck: null,
  });

  useEffect(() => {
    checkAllServices();
    const interval = setInterval(checkAllServices, 30000);
    return () => clearInterval(interval);
  }, []);

  const checkAllServices = async () => {
    await Promise.all([
      checkOpenAIStatus(),
      checkLeonardoStatus(),
    ]);
  };

  const checkOpenAIStatus = async () => {
    try {
      setOpenaiState(prev => ({ ...prev, status: 'checking' }));

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setOpenaiState({ status: 'unknown', lastCheck: new Date() });
        return;
      }

      const { data: openaiConfig, error: configError } = await supabase
        .from('system_config')
        .select('value')
        .eq('key', 'openai_api_key')
        .maybeSingle();

      if (configError) {
        console.error('Failed to check OpenAI config:', configError);
        setOpenaiState({ status: 'unknown', lastCheck: new Date() });
        return;
      }

      if (!openaiConfig || !openaiConfig.value) {
        setOpenaiState({ status: 'unavailable', lastCheck: new Date() });
        return;
      }

      setOpenaiState({ status: 'available', lastCheck: new Date() });
    } catch (error) {
      console.error('OpenAI health check failed:', error);
      setOpenaiState({ status: 'unknown', lastCheck: new Date() });
    }
  };

  const checkLeonardoStatus = async () => {
    try {
      setLeonardoState(prev => ({ ...prev, status: 'checking' }));

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setLeonardoState({ status: 'unknown', lastCheck: new Date() });
        return;
      }

      const { data: leonardoConfig, error: configError } = await supabase
        .from('system_config')
        .select('value')
        .eq('key', 'leonardo_api_key')
        .maybeSingle();

      if (configError) {
        console.error('Failed to check Leonardo config:', configError);
        setLeonardoState({ status: 'unknown', lastCheck: new Date() });
        return;
      }

      if (!leonardoConfig || !leonardoConfig.value) {
        setLeonardoState({ status: 'unavailable', lastCheck: new Date() });
        return;
      }

      setLeonardoState({ status: 'available', lastCheck: new Date() });
    } catch (error) {
      console.error('Leonardo health check failed:', error);
      setLeonardoState({ status: 'unknown', lastCheck: new Date() });
    }
  };

  const getStatusConfig = (status: ServiceStatus) => {
    switch (status) {
      case 'available':
        return {
          icon: CheckCircle,
          color: 'green',
          bgClass: 'bg-green-100',
          textClass: 'text-green-600',
          borderClass: 'border-green-200',
          label: 'Beschikbaar',
        };
      case 'unavailable':
        return {
          icon: AlertCircle,
          color: 'red',
          bgClass: 'bg-red-100',
          textClass: 'text-red-600',
          borderClass: 'border-red-200',
          label: 'Niet Beschikbaar',
        };
      case 'checking':
        return {
          icon: Activity,
          color: 'blue',
          bgClass: 'bg-blue-100',
          textClass: 'text-blue-600',
          borderClass: 'border-blue-200',
          label: 'Controleren...',
        };
      default:
        return {
          icon: HelpCircle,
          color: 'gray',
          bgClass: 'bg-gray-100',
          textClass: 'text-gray-600',
          borderClass: 'border-gray-200',
          label: 'Onbekend',
        };
    }
  };

  const renderServiceCard = (name: string, state: ServiceState) => {
    const config = getStatusConfig(state.status);
    const Icon = config.icon;

    return (
      <div className={`p-6 bg-white border ${config.borderClass} rounded-xl`}>
        <div className="flex items-center gap-3 mb-3">
          <div className={`p-2 ${config.bgClass} rounded-lg`}>
            <Icon className={`w-5 h-5 ${config.textClass} ${state.status === 'checking' ? 'animate-pulse' : ''}`} />
          </div>
          <span className="text-sm text-gray-600 font-medium">{name}</span>
        </div>
        <div className={`text-2xl font-semibold ${config.textClass}`}>{config.label}</div>
        {state.lastCheck && (
          <div className="mt-2 text-xs text-gray-500">
            Laatste check: {state.lastCheck.toLocaleTimeString('nl-NL')}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {renderServiceCard('OpenAI', openaiState)}
      {renderServiceCard('Leonardo AI', leonardoState)}
    </>
  );
}
